import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useSyncExternalStore, type SetStateAction } from 'react';
import { MyCoachContinuityStore, type ContinuityLease } from '@/lib/myCoachContinuity';

export const MyCoachContinuityContext = createContext<{ actorId: string | null; store: MyCoachContinuityStore; local?: boolean } | null>(null);

export function useContinuityUnloadWarning(store: MyCoachContinuityStore, enabled = true) {
  useEffect(() => {
    if (!enabled) return;
    const warn = (event: BeforeUnloadEvent) => {
      if (!store.hasDirty()) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [store, enabled]);
}

export function useContinuityActor() { const context=useContext(MyCoachContinuityContext);return context?.local?undefined:context?.actorId; }
export function useHasContinuityBoundary() { return useContext(MyCoachContinuityContext) !== null; }

const noSubscribe = () => () => {};
const zeroVersion = () => 0;
/** Metadata-only cleanup for drafts whose action or relationship disappeared. */
export function useContinuityDraftScopes(scope: readonly string[], readable: boolean) {
  const context = useContext(MyCoachContinuityContext);
  const store = context?.store;
  useSyncExternalStore(store?.subscribe ?? noSubscribe, store?.version ?? zeroVersion, zeroVersion);
  const prefix = JSON.stringify([context?.actorId ?? 'mounted', ...scope]).slice(0, -1) + ',';
  const leases = readable && context?.actorId ? store?.dirtyLeases(prefix) ?? [] : [];
  return { leases, discard: (lease: ContinuityLease) => store?.clearIfCurrent(lease) };
}

export function useContinuityDraft<T>({ scope, binding, readable, baseline, equals, retainMountedOnRevisionChange=false }: {
  scope: readonly (string | null | undefined)[];
  binding: string | null;
  readable: boolean;
  baseline: T;
  equals: (left: T, right: T) => boolean;
  retainMountedOnRevisionChange?: boolean;
}) {
  const context = useContext(MyCoachContinuityContext);
  // Isolated component tests/embeds retain mounted local-state semantics.
  const local = useMemo(() => new MyCoachContinuityStore(), []);
  const store = context?.store ?? local;
  const key = JSON.stringify([context?.actorId ?? 'mounted', ...scope]);
  const allowed = readable && binding !== null && (!context || context.actorId !== null);
  const lifetime = useRef({ mounted: true, store, key, binding, allowed });
  lifetime.current = { ...lifetime.current, store, key, binding, allowed };
  useEffect(() => { lifetime.current.mounted = true; return () => { lifetime.current.mounted = false; }; }, []);
  const getSnapshot = useCallback(() => store.snapshot<T>(key), [store, key]);
  const entry = useSyncExternalStore(store.subscribe, getSnapshot, () => undefined);
  const pendingSnapshot = useCallback(() => store.isSubmitting(key), [store, key]);
  const submitting = useSyncExternalStore(store.subscribe, pendingSnapshot, () => false);
  const matches = allowed && entry?.lease.binding === binding;
  const exposed=useRef<ContinuityLease>();
  if(matches)exposed.current=entry.lease;
  const retained=Boolean(allowed&&retainMountedOnRevisionChange&&entry?.dirty&&exposed.current===entry.lease);
  const value = matches||retained ? entry!.value : baseline;
  const dirty = Boolean((matches||retained) && entry?.dirty);
  const conflict = Boolean(allowed && entry?.dirty && !matches);
  const editingBinding=retained&&entry?entry.lease.binding:binding;
  useContinuityUnloadWarning(local, !context);

  const update = useCallback((action: SetStateAction<T>) => {
    const currentScope = lifetime.current;
    if (!allowed || binding === null || editingBinding===null || !currentScope.mounted || !currentScope.allowed
      || currentScope.store !== store || currentScope.key !== key || currentScope.binding !== binding) return;
    const current = store.snapshot<T>(key);
    if (current?.dirty && current.lease.binding !== editingBinding) return;
    if(retained&&current?.lease.binding!==editingBinding)return;
    const previous = current?.lease.binding === editingBinding ? current.value : baseline;
    const next = typeof action === 'function' ? (action as (value: T) => T)(previous) : action;
    const nextLease = store.write(key, editingBinding, next, !equals(next, baseline));
    if (retained) exposed.current = nextLease;
  }, [allowed, binding, editingBinding, retained, store, key, baseline, equals]);
  const capture = () => matches ? entry.lease : undefined;
  const clearIfCurrent = useCallback((lease: ContinuityLease | undefined) => store.clearIfCurrent(lease), [store]);
  const markCleanIfCurrent = useCallback((lease: ContinuityLease | undefined, value: T) => store.markCleanIfCurrent(lease, value), [store]);
  const discard = () => { if (allowed) store.clearIfCurrent(entry?.lease); };
  const beginSubmission = () => allowed && binding !== null && lifetime.current.allowed && lifetime.current.mounted
    && lifetime.current.store === store && lifetime.current.key === key && lifetime.current.binding === binding
    ? store.beginSubmission(key, binding) : undefined;
  return { value, dirty, conflict, submitting, beginSubmission, isCurrentSubmission: store.isCurrentSubmission, endSubmission: store.endSubmission, update, capture, clearIfCurrent, markCleanIfCurrent, discard };
}
